// Cliente HTTP com gestão automática de access/refresh tokens.
// Storage: localStorage (foundation week). Refinaremos pra cookies httpOnly em PR futuro se necessário.

const ACCESS_KEY = "novavida.accessToken"
const REFRESH_KEY = "novavida.refreshToken"

export const tokenStore = {
  getAccess: () => localStorage.getItem(ACCESS_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  set: (access: string, refresh: string) => {
    localStorage.setItem(ACCESS_KEY, access)
    localStorage.setItem(REFRESH_KEY, refresh)
  },
  setAccess: (access: string) => localStorage.setItem(ACCESS_KEY, access),
  clear: () => {
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
  },
}

const API_BASE = "/api" // proxied to backend by vite.config.ts

class ApiError extends Error {
  constructor(
    public status: number,
    public payload: unknown,
    message?: string
  ) {
    super(message ?? `HTTP ${status}`)
  }
}

let refreshPromise: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    const refresh = tokenStore.getRefresh()
    if (!refresh) return null

    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken: refresh }),
    })

    if (!res.ok) {
      tokenStore.clear()
      return null
    }

    const data = (await res.json()) as { accessToken: string; refreshToken: string }
    tokenStore.set(data.accessToken, data.refreshToken)
    return data.accessToken
  })()

  try {
    return await refreshPromise
  } finally {
    refreshPromise = null
  }
}

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit & { skipAuth?: boolean } = {}
): Promise<T> {
  const { skipAuth, ...rest } = options
  const headers = new Headers(rest.headers)
  if (!headers.has("Content-Type") && rest.body) {
    headers.set("Content-Type", "application/json")
  }
  if (!skipAuth) {
    const access = tokenStore.getAccess()
    if (access) headers.set("Authorization", `Bearer ${access}`)
  }

  let res = await fetch(`${API_BASE}${path}`, { ...rest, headers })

  if (res.status === 401 && !skipAuth) {
    const newAccess = await refreshAccessToken()
    if (newAccess) {
      headers.set("Authorization", `Bearer ${newAccess}`)
      res = await fetch(`${API_BASE}${path}`, { ...rest, headers })
    }
  }

  const contentType = res.headers.get("content-type") ?? ""
  const isJson = contentType.includes("application/json")
  const payload = isJson ? await res.json() : await res.text()

  if (!res.ok) {
    throw new ApiError(res.status, payload)
  }
  return payload as T
}

export { ApiError }
